import { getKey } from "@redotech/react-util/key";
import { useTranslation } from "@redotech/redo-i18n";
import {
  IMPERIAL_DISTANCE_COUNTRIES,
  milesToKm,
} from "@redotech/redo-model/distance-conversion";
import { InteractiveMapLocation } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckboxCard } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import * as interactiveMap from "./in-store-locations.module.css";

export interface InStoreLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

const LocationsModal = memo(function LocationsModal({
  pending,
  error,
  locations,
  customerCountry,
}: {
  pending: boolean;
  error: boolean;
  locations: InteractiveMapLocation[] | null;
  customerCountry?: string;
}) {
  const { t } = useTranslation();

  if (pending) {
    return <LoadingRedoAnimation />;
  }
  if (error) {
    return <Text textColor="error">{t("Error loading locations")}</Text>;
  }
  if (!locations) {
    return null;
  }

  return (
    <InteractiveMap
      customerCountry={customerCountry}
      key={getKey(locations)}
      locations={locations}
    />
  );
});

function formatDistance(
  distanceMiles: string | null | undefined,
  country: string,
): string | null {
  if (!distanceMiles) {
    return null;
  }
  const miles = parseFloat(distanceMiles);
  if (isNaN(miles)) {
    return null;
  }
  if (IMPERIAL_DISTANCE_COUNTRIES.has(country)) {
    return `${miles} miles`;
  }
  return `${milesToKm(miles).toFixed(1)} km`;
}

const LocationsList = memo(function LocationsList({
  locations,
  selectedIndex,
  selectLocation,
  customerCountry = "US",
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
  customerCountry?: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.children[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedIndex]);

  if (!locations.length) {
    return <div>{t("No locations found")}</div>;
  }
  return (
    <div className={interactiveMap.locationsTextContainer} ref={ref}>
      <Flex dir="column" gap="2xl" p="xl">
        {locations.map((location, i) => (
          <RedoCheckboxCard
            checked={selectedIndex === i}
            key={i}
            onClick={() => selectLocation(i)}
            title={`${i + 1}. ${location.name}`}
          >
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" textColor="secondary">
                {(() => {
                  const dist = formatDistance(
                    location.distance,
                    customerCountry,
                  );
                  return dist ? (
                    <span>{t("({{distance}})", { distance: dist })}</span>
                  ) : null;
                })()}
              </Text>
              <Text fontSize="sm" textColor="secondary">
                {location.address.address1}
              </Text>
              {location.address.address2 && (
                <Text fontSize="sm" textColor="secondary">
                  {location.address.address2}
                </Text>
              )}
              <Text fontSize="sm" textColor="secondary">
                {location.address.city}, {location.address.state || ""}{" "}
                {location.address.zip}
              </Text>
            </Flex>
          </RedoCheckboxCard>
        ))}
      </Flex>
    </div>
  );
});

const LocationMap = memo(function LocationMap({
  locations,
  selectedIndex,
  selectLocation,
}: {
  locations: InteractiveMapLocation[];
  selectedIndex: number;
  selectLocation: (index: number) => void;
}) {
  const map = useMap();
  const positions = useMemo(() => {
    return locations.map((location) =>
      location.geo !== undefined
        ? { lat: location.geo!.latitude, lng: location.geo!.longitude }
        : undefined,
    );
  }, [locations]);

  useEffect(() => {
    if (map && locations[selectedIndex]?.geo) {
      map.setCenter({
        lat: locations[selectedIndex].geo!.latitude,
        lng: locations[selectedIndex].geo!.longitude,
      });
    }
  }, [map, selectedIndex, locations]);

  if (locations[selectedIndex]?.geo === undefined) {
    return null;
  }

  return (
    <Flex className={interactiveMap.mapContainer}>
      <Map
        defaultCenter={positions[0]}
        defaultZoom={12}
        fullscreenControl={false}
        mapId="37a5dbe51230f056"
        mapTypeControl={false}
        streetViewControl={false}
      >
        {positions.map(
          (position, i) =>
            position && (
              <AdvancedMarker
                key={i}
                onClick={() => selectLocation(i)}
                position={position}
                zIndex={i === selectedIndex ? 10_000 : i}
              >
                <Pin borderColor="black" scale={i === selectedIndex ? 1.25 : 1}>
                  <span
                    style={{ fontSize: i === selectedIndex ? "18px" : "14px" }}
                  >
                    {i + 1}
                  </span>
                </Pin>
              </AdvancedMarker>
            ),
        )}
      </Map>
    </Flex>
  );
});

export const InStoreLocationsButton = memo(function InStoreLocationsButton({
  draftReturnId,
  returnId,
  customerCountry,
  searchInStoreLocations,
}: {
  draftReturnId?: string;
  returnId?: string;
  customerCountry?: string;
  searchInStoreLocations: (
    params: InStoreLocationsSearchParams,
  ) => Promise<InteractiveMapLocation[]>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const query = useQuery({
    queryKey: ["inStoreLocations", draftReturnId, returnId],
    queryFn: () => searchInStoreLocations({ draftReturnId, returnId }),
    enabled: isOpen,
    staleTime: Infinity,
  });

  return (
    <Flex>
      <RedoButton
        hierarchy="secondary"
        onClick={() => setIsOpen(true)}
        text={t("See available locations")}
      />
      <RedoModal
        isOpen={isOpen}
        onModalCloseRequested={() => setIsOpen(false)}
        title={t("Find a store that accepts drop offs")}
      >
        <LocationsModal
          customerCountry={customerCountry}
          error={!!query.error}
          locations={query.data ?? null}
          pending={query.isPending && query.fetchStatus !== "idle"}
        />
      </RedoModal>
    </Flex>
  );
});

export const InteractiveMap = memo(function InteractiveMap({
  locations,
  customerCountry,
}: {
  locations: InteractiveMapLocation[];
  customerCountry?: string;
}) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  if (!locations.length) {
    return <div>{t("No locations found")}</div>;
  }

  return (
    <div className={interactiveMap.locationsContainer}>
      <LocationsList
        customerCountry={customerCountry}
        locations={locations}
        selectedIndex={selectedIndex}
        selectLocation={setSelectedIndex}
      />
      <APIProvider apiKey="AIzaSyBLnX3xDv_dzLIUsaKBZDW4vNJnpimI31M">
        <LocationMap
          locations={locations}
          selectedIndex={selectedIndex}
          selectLocation={setSelectedIndex}
        />
      </APIProvider>
    </div>
  );
});
